<template>
        <router-view v-slot="{ Component }">
                <transition name="fade" mode="out-in">
                        <component :is="Component" />
                </transition>
        </router-view>
        <Footer></Footer>
</template>

<script>
import axios from "axios";
import Footer from "@/components/Footer.vue";
import { getMomentTimezone } from './utils/dateUtils';

export default {
        name: "App",
        title: "Native American Tours",
        components: {
                Footer
        },
        data() {
                return {
                        iframeStatus: true,
                        tenantId: null,
                        tourOperatorId: 0,
                        packageId: 0,
                        affiliateId: 0,
                        comboIds: 0,
                        date: null,
                        timezone: "America/Phoenix"
                }
        },
        mounted() {
                
                // Clear URL parameters that contain pre-filled form data
                const urlParams = JSON.parse(localStorage.getItem('urlParams') || '{}');
                if (urlParams.timeSlot || urlParams.rateGroupIds || urlParams.date) {
                        // Remove form-related URL parameters while keeping essential ones
                        const clearedParams = {
                                tenantId: urlParams.tenantId,
                                tourOperatorId: urlParams.tourOperatorId,
                                packageId: urlParams.packageId,
                                affiliateId: urlParams.affiliateId,
                                comboIds: urlParams.comboIds
                        };
                        localStorage.setItem('urlParams', JSON.stringify(clearedParams));
                }
                
                // Clear form data from Vuex store
                this.$store.dispatch('storeFormData', null);
                
                // Clear rate group counters from Vuex store
                this.$store.dispatch('storeRateGroupCounters', {});
                
                // Clear slot-related data from Vuex store
                this.$store.dispatch('storeSlotId', 0);
                this.$store.dispatch('storeTimeDate', null);

        },
        async created() {
                // Parse URL parameters
                let uri = window.location.search.substring(1);
                let params = new URLSearchParams(uri);

                // Check if any URL parameters exist
                const hasUrlParams = params.size > 0;

                // Load stored parameters from localStorage with validation
                let storedParams = {};
                try {
                        storedParams = JSON.parse(localStorage.getItem('urlParams') || '{}');
                        // Validate that storedParams is an object
                        if (typeof storedParams !== 'object' || storedParams === null || Array.isArray(storedParams)) {
                                console.log('Invalid urlParams in localStorage, using defaults');
                                storedParams = {};
                        }
                } catch (error) {
                        console.log('Failed to parse urlParams from localStorage, using defaults:', error);
                        storedParams = {};
                }

                // If URL parameters exist, clear old stored parameters to avoid using stale data
                if (hasUrlParams) {
                        storedParams = {}; // Reset stored parameters
                        localStorage.removeItem('urlParams'); // Clear localStorage
                }

                // Determine if URL specifies a single package (pid) or combo (cids)
                const hasPid = params.get("pid") !== null;
                const hasCids = params.get("cids") !== null;
                const hasAid = params.get("aid") !== null;

                // Check if Vuex state was already restored from localStorage (page refresh scenario)
                const stateRestored = this.$store.state.tenantId !== null;

                if (hasUrlParams || !stateRestored) {
                        // Use URL parameters if present, otherwise fall back to stored parameters or defaults
                        this.tenantId = params.get("tid") || (storedParams.tenantId ?? null);
                        this.$store.dispatch('storeTenantId', this.tenantId);

                        // Update axios baseURL with correct tenantId
                        if (this.tenantId) {
                                axios.defaults.baseURL = `https://${this.tenantId}.${process.env.VUE_APP_API_URL}`;
                        }

                        this.tourOperatorId = params.get("oid") ? parseInt(params.get("oid")) : (storedParams.tourOperatorId ?? 0);
                        this.$store.dispatch('storeTourOperatorId', this.tourOperatorId);

                        this.packageId = hasPid ? parseInt(params.get("pid")) : (hasCids ? 0 : (storedParams.packageId ?? 0));
                        this.$store.dispatch('storePackageId', this.packageId);

                        this.affiliateId = hasAid ? parseInt(params.get("aid")) : (hasUrlParams ? 0 : (storedParams.affiliateId ?? 0));
                        this.$store.dispatch('storeAffiliateId', this.affiliateId);

                        this.comboIds = hasCids ? params.get("cids") : (hasPid ? 0 : (storedParams.comboIds ?? 0));
                        this.$store.dispatch('storeComboIds', this.comboIds);
                } else {
                        // Sync local variables from already-restored store without dispatching
                        this.tenantId = this.$store.state.tenantId;
                        this.tourOperatorId = this.$store.state.tourOperatorId;
                        this.packageId = this.$store.state.packageId;
                        this.affiliateId = this.$store.state.affiliateId;
                        this.comboIds = this.$store.state.comboIds;
                        
                        // Update axios baseURL with correct tenantId
                        if (this.tenantId) {
                                axios.defaults.baseURL = `https://${this.tenantId}.${process.env.VUE_APP_API_URL}`;
                        }
                }

                // Expected date format: yyyymmdd (e.g., 20260203 for Feb 3, 2026)
                const EXPECTED_DATE_LENGTH = 8;
                let urlDate = null;
                const dateParam = params.get("date");
                
                // Validate date format: must be exactly 8 digits
                if (dateParam && dateParam.length === EXPECTED_DATE_LENGTH && /^\d{8}$/.test(dateParam)) {
                        try {
                                // Convert yyyymmdd to YYYY-MM-DD
                                const year = dateParam.substring(0, 4);
                                const month = dateParam.substring(4, 6);
                                const day = dateParam.substring(6, 8);
                                const formattedDate = `${year}-${month}-${day}`;
                                
                                // Use default timezone for initial validation
                                const defaultTimezone = this.$store.state.timezone || 'America/Phoenix';
                                const parsedDate = getMomentTimezone(defaultTimezone, formattedDate);
                                const today = getMomentTimezone(defaultTimezone);

                                // Only accept valid dates that are today or in the future
                                if (parsedDate.isValid() && parsedDate.isSameOrAfter(today, 'day')) {
                                        urlDate = formattedDate;
                                } else {
                                        console.log(`Invalid or past date in URL: ${dateParam}, using current date instead`);
                                }
                        } catch (error) {
                                console.log(`Error parsing date from URL: ${dateParam}`, error);
                        }
                } else if (dateParam) {
                        console.log(`Invalid date format in URL: ${dateParam} (expected 8 digits in yyyymmdd format)`);
                }

                // Set date in store IMMEDIATELY if we have a URL date
                if (urlDate) {
                        this.date = urlDate;
                        this.$store.dispatch('storeDate', this.date);
                } else if (storedParams.date) {
                        // Use stored date temporarily (will be validated after timezone fetch)
                        this.date = storedParams.date;
                        this.$store.dispatch('storeDate', this.date);
                } else {
                        // No URL date or stored date - use current date
                        const defaultTimezone = this.$store.state.timezone || 'America/Phoenix';
                        const today = getMomentTimezone(defaultTimezone);
                        const isDixies = this.$store.state.tenantId === 'dixies' && today.year() === 2025;
                        this.date = isDixies ? getMomentTimezone(defaultTimezone, [2026, 0, 1]).format('YYYY-MM-DD') : today.format('YYYY-MM-DD');
                        this.$store.dispatch('storeDate', this.date);
                }

                const response = await axios.get("/tour-operator-logo/" + this.tourOperatorId);
                this.timezone = response.data.timezone;
                this.$store.dispatch('storeTimezone', this.timezone);

                // Store tour operator logo and company name
                this.$store.dispatch('storeTourPackageLogo', {
                        logo: response.data.TourOperatorLogo,
                        name: response.data.name
                });

                // Parse time-slot from URL with validation
                const timeSlotParam = params.get("time-slot");
                let urlTimeSlot = null;
                if (timeSlotParam) {
                        const parsed = parseInt(timeSlotParam);
                        // Validate: must be a positive integer within reasonable bounds
                        if (!isNaN(parsed) && parsed > 0) {
                                urlTimeSlot = parsed;
                        } else {
                                console.log(`Invalid time-slot in URL: ${timeSlotParam} (must be a positive integer)`);
                        }
                }

                // Parse dynamic rategroupid[ID]=VALUE parameters with validation
                const urlRateGroupIds = {};
                const MAX_COUNTER_VALUE = 999999; // Maximum reasonable number of guests
                for (const [key, value] of params.entries()) {
                        const match = key.match(/^rategroupid\[(\d+)\]$/);
                        if (match) {
                                const rateGroupId = parseInt(match[1]);
                                const count = parseInt(value);
                                
                                // Validate: both ID and count must be valid positive integers
                                if (!isNaN(rateGroupId) && rateGroupId > 0 && 
                                    !isNaN(count) && count >= 0 && count <= MAX_COUNTER_VALUE) {
                                        urlRateGroupIds[rateGroupId] = count;
                                } else {
                                        console.log(`Invalid rate group parameter: ${key}=${value} (ID and count must be positive integers, count max ${MAX_COUNTER_VALUE})`);
                                }
                        }
                }

                // Final date validation with proper timezone (if date wasn't set earlier, set it now)
                if (!this.date) {
                        const today = getMomentTimezone(this.$store.state.timezone);
                        const isDixies = this.$store.state.tenantId === 'dixies' && today.year() === 2025;
                        const currentDate = isDixies ? getMomentTimezone(this.$store.state.timezone, [2026, 0, 1]).format('YYYY-MM-DD') : getMomentTimezone(this.$store.state.timezone).format('YYYY-MM-DD');
                        
                        this.date = currentDate;
                        this.$store.dispatch('storeDate', this.date);
                }

                // Save parameters to localStorage
                // Only store timeSlot and rateGroupIds if they were actually provided in URL
                const paramsToStore = {
                        tenantId: this.tenantId,
                        tourOperatorId: this.tourOperatorId,
                        packageId: this.packageId,
                        affiliateId: this.affiliateId,
                        comboIds: this.comboIds,
                        date: this.date
                };
                
                // Only add URL-specific parameters if they exist
                if (urlTimeSlot !== null) {
                        paramsToStore.timeSlot = urlTimeSlot;
                }
                if (Object.keys(urlRateGroupIds).length > 0) {
                        paramsToStore.rateGroupIds = urlRateGroupIds;
                }
                
                localStorage.setItem('urlParams', JSON.stringify(paramsToStore));

                // Reset cartItem for non-combo URLs
                if (hasPid && !hasCids) {
                        this.$store.dispatch('storeCartItem', {});
                }
        }
};
</script>

<!-- <style>
@import './assets/css/main.css';
</style> -->

<!--
<style>
@import './assets/css/design-system.css';
@import './assets/css/style.css';

#app {
        font-family: var(--font-body);
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        color: var(--neutral-800);
        background: var(--neutral-50);
}

/* Page Transitions */
.fade-enter-active,
.fade-leave-active {
        transition: opacity var(--transition-base), transform var(--transition-base);
}

.fade-enter-from {
        opacity: 0;
        transform: translateY(10px);
}

.fade-leave-to {
        opacity: 0;
        transform: translateY(-10px);
}

.fade-enter-to,
.fade-leave-from {
        opacity: 1;
        transform: translateY(0);
}
</>
-->